import { FC, useCallback, useState } from "react";
import { GiOilDrum } from "react-icons/gi";
import { LuListOrdered } from "react-icons/lu";
import { useParams } from "react-router-dom";
import { Button, Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../../../components/buttons/DeletePopover";
import { usePrivilege } from "../../../../../components/priviledge/PriviledgeProvider";
import CustomTable from "../../../../../components/table/CustomTable";
import { FilterDrawerView } from "../../../../../components/table/components/TableFilter";
import useFetch from "../../../../../hooks/useFetch";
import useFilters from "../../../../../hooks/useFilters";
import Page from "../../../../../layout/Page/Page";
import { RouteService } from "../../../../../services/route/routeService";
import { Headquarter } from "../../../../../type/entities/headquarter-type";
import cn from "../../../../../utils/classNames";
import AddHeadquarterToRouteModal from "./AddHeadquarterToRouteModal";
import RouteHeadquartersFilters from "./RouteHeadquartersFilters";

interface RouteHeadquartersListProps {
}

const columnOrder = [
    'id',
    'name',
    'address',
    'city',
    'drumsNumber',
    'pickBetweenDays',
    'highSeason',
    'headquarter',
    'provider',
    'active',
    'orderIndex',
];

const RouteHeadquarters: FC<RouteHeadquartersListProps> = () => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFormDrawer, setOpenFormDrawer] = useState(false);
    const [headquarterSelected, setHeadquarterSelected] = useState<Headquarter | null>(null);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new RouteService();
    const { userCan } = usePrivilege();

    // HOOKS

    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LOS DATOS DE LA RUTA SELECCIONADA
     * @EN GETS THE SELECTED ROUTE DATA
     */
    // -------------------------------------------------------------------------------------------------------------
    const [routeData, routeLoading, routeError, routeRefetch] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.getRouteById(id);
        return response.getResponseData();
    }, [id]));
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO PAGINADO DE RELACIONES RUTA-SEDE
     * @EN GETS THE PAGINATED LIST OF ROUTE-HEADQUARTERS RELATIONSHIPS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id) return null;
        const newFilters = { ...filters, filter_filters: { ...filters.filter_filters, routeId: id } };
        const response = await service.listRouteHeadquarters(newFilters);
        return response.getResponseData();
    }, [id, filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACTUALIZA LOS ORDENES DE LAS SUCURSALES DE UNA RUTA
     * @EN UPDATES THE ORDER INDEXES OF HEADQUARTERS FOR A ROUTE
     */
    //-------------------------------------------------------------------------------------------------------------
    const updateRouteHeadquarters = async (value: any) => {
        if (!userCan('reorder', 'route')) {
            toast.error('No tienes permisos para reordenar las sedes');
            return;
        }

        const useRowIndex = filters.filter_order?.[0]?.field || Object.keys(filters.filter_filters ?? {}).length > 0;
        const headquarterId = value.activeRowData?.original?.headquarter?.id;
        const currentIndex = useRowIndex ? value.activeRowData?.original?.orderIndex : value.active?.id ?? null;
        const newIndex = useRowIndex ? value.overRowData.original.orderIndex : parseInt(value.over?.id ?? '0');

        try {
            const response = await service.updateHeadquarterPosition(id, headquarterId, newIndex);
            const responseData = response?.getResponseData();
            if (responseData && responseData.success) {
                toast.success('Sedes actualizadas exitosamente');
                refetch();
            } else {
                toast.error('Error al actualizar las sedes');
            }
        } catch (error) {
            console.error('Error updating route headquarters:', error);
            toast.error('Error al actualizar las sedes');
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA UNA SUCURSAL DE UNA RUTA
     * @EN DELETES A HEADQUARTER FROM A ROUTE
     */
    //-------------------------------------------------------------------------------------------------------------
    const deleteHeadquarterFromRoute = async (routeHeadquarterId: string) => {
        if (!userCan('delete', 'route')) {
            toast.error('No tienes permisos para eliminar sedes de la ruta');
            return;
        }
        try {
            const response = await service.removeHeadquarterFromRoute(routeHeadquarterId);
            const responseData = response?.getResponseData();
            if (responseData && responseData.success) {
                toast.success('Sede desvinculada exitosamente');
            } else {
                toast.error('Error al desvincular la sede');
            }
        } catch (error) {
            console.error('Error removing headquarter from route:', error);
            toast.error('Error al desvincular la sede');
        } finally {
            refetch();
            routeRefetch();
        }
    }
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data?.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------


    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'route-headquarters-table'}
                    divClassName='max-h-table-view'
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable={true}
                    onDragEnd={(value: any) => updateRouteHeadquarters(value)}
                    columnsNoSortables={[]}
                    columnsNotShown={['id', 'headquarter', 'orderIndex', 'name', 'address', 'city', 'drumsNumber', 'pickBetweenDays', 'highSeason', 'provider', 'active', 'headquarter',]}
                    additionalColumns={[
                        {
                            id: 'name',
                            header: 'Nombre',
                            render: (row: any) => {
                                const headquarter = row.row.original.headquarter;
                                return (
                                    <div>
                                        {headquarter?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            id: 'address',
                            header: 'Dirección',
                            render: (row: any) => {
                                const headquarter = row.row.original.headquarter;
                                return (
                                    <div>
                                        {headquarter?.address}
                                    </div>
                                );
                            }
                        },
                        {
                            id: 'city',
                            header: 'Ciudad',
                            enableSorting: true,
                            render: (row: any) => {
                                const headquarter = row.row.original.headquarter;
                                return (
                                    <div>
                                        {headquarter?.city}
                                    </div>
                                );
                            }
                        },
                        /*
                        {
                            id: 'drumsNumber',
                            header: 'Número de bidones',
                            render: (row: any) => {
                                const headquarter = row.row.original.headquarter;
                                return (
                                    <span className="flex flex-nowrap gap-2 capitalize items-center">
                                        <GiOilDrum className="text-gray-400 min-h-[15px] min-w-[15px]" />
                                        {headquarter?.drumsNumber}
                                    </span>
                                );
                            }
                        },
                        */
                        {
                            id: 'pickBetweenDays',
                            header: 'Días de recogida',
                            render: (row: any) => {
                                const headquarter = row.row.original.headquarter;
                                return (
                                    <div>
                                        {headquarter?.pickBetweenDays}
                                    </div>
                                );
                            }
                        },
                        {
                            id: 'highSeason',
                            header: 'Temporada alta',
                            render: (row: any) => {
                                const headquarter = row.row.original.headquarter;
                                return (
                                    <div>
                                        {headquarter?.highSeason ? 'Sí' : 'No'}
                                    </div>
                                );
                            }
                        },
                        {
                            id: 'provider',
                            header: 'Proveedor',
                            render: (row: any) => {
                                const headquarter = row.row.original.headquarter;
                                return (
                                    <div>
                                        {headquarter?.provider?.companyName}
                                    </div>
                                );
                            }
                        },
                        {
                            id: 'orderIndex',
                            header: 'Orden',
                            enableSorting: true,
                            render: (row: any) => {
                                return (
                                    <div className="flex items-center gap-2 items-center">
                                        <LuListOrdered className="text-gray-400 min-h-[15px] min-w-[15px]" />
                                        {row.row.original.orderIndex}
                                    </div>
                                );
                            }
                        }
                        // {
                        //     id: 'active',
                        //     header: 'Activo',
                        //     render: (row: any) => {
                        //         const headquarter = row.row.original.headquarter;

                        //         return (
                        //             <Tooltip
                        //                 size="sm"
                        //                 content={headquarter.active ? 'Desactivar' : 'Activar'}
                        //                 placement="top"
                        //                 color="invert"
                        //             >
                        //                 <Switch
                        //                     id={row.row.original.id}
                        //                     checked={headquarter.active}
                        //                     className='pointer-events-none'
                        //                     switchKnobClassName='bg-secondary'
                        //                 />
                        //             </Tooltip>
                        //         );
                        //     },
                        // },
                    ]}
                    actions={[
                        {
                            label: 'Delete',
                            permissions: { group: 'route', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar relación'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar relación`}
                                                description={`¿Estás seguro de que deseas eliminar la relación entre la ruta y ` + row.headquarter.name + ` ?`}
                                                onDelete={() => deleteHeadquarterFromRoute(row.id)}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data?.lastPage || 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    {
                        userCan('assign_headquarters', 'route') && routeData?.status &&
                        (
                            <Button
                                color="primary"
                                onClick={() => {
                                    setOpenFormDrawer(true)
                                    setHeadquarterSelected(null)
                                }}
                                className={cn(['mt-4 md:mt-0 mx-2'])}
                            >
                                Añadir Sede
                            </Button>
                        )
                    }
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Asignaciones Sede'} onClear={() => resetFilters(50)}>
                <RouteHeadquartersFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            {!loading &&
                <AddHeadquarterToRouteModal
                    isOpen={openFormDrawer}
                    onClose={() => setOpenFormDrawer(false)}
                    routeId={id}
                    onSuccess={() => {
                        refetch();
                        routeRefetch();
                    }}
                />}

        </Page>
    );
};

export default RouteHeadquarters;